import React, { useEffect, useRef } from "react";

const AmbientBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId;
    const mouse = { x: -1000, y: -1000, active: false };

    let particles = [];
    let sparks = [];
    let ripples = [];
    const maxDistance = 115; // Connection distance limit
    const mouseConnectionRadius = 160;

    const initParticles = () => {
      const w = canvas.width;
      const h = canvas.height;
      // Scale count based on screen area: ~80 on desktop, ~35 on mobile
      const count = Math.min(Math.floor((w * h) / 18000), 85);
      
      particles = [];
      for (let i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * w,
          y: Math.random() * h,
          vx: (Math.random() - 0.5) * 0.45,
          vy: (Math.random() - 0.5) * 0.45,
          radius: Math.random() * 2 + 1.2
        });
      }
    };

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initParticles();
    };

    const drawSimulation = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const w = canvas.width;
      const h = canvas.height;

      // 0. Update and draw stardust sparks trail
      sparks.forEach((s) => {
        s.x += s.vx;
        s.y += s.vy;
        s.alpha -= 0.02; // fade rate

        ctx.beginPath();
        ctx.arc(s.x, s.y, s.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(99, 102, 241, ${s.alpha * 0.38})`;
        ctx.fill();
      });
      sparks = sparks.filter((s) => s.alpha > 0);

      // 0.1 Update and draw expanding ripples (shockwaves)
      ripples.forEach((r) => {
        r.radius += 5.5; // expansion speed
        r.alpha -= 0.022; // fade speed

        // Push particles away from the expanding wave front
        particles.forEach((p) => {
          const dx = p.x - r.x;
          const dy = p.y - r.y;
          const dist = Math.hypot(dx, dy);
          if (Math.abs(dist - r.radius) < 22) {
            const force = (22 - Math.abs(dist - r.radius)) / 22;
            p.vx += (dx / dist) * force * 0.55;
            p.vy += (dy / dist) * force * 0.55;
          }
        });

        // Draw ripple circle
        ctx.beginPath();
        ctx.arc(r.x, r.y, r.radius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(99, 102, 241, ${r.alpha * 0.22})`;
        ctx.lineWidth = 2.2;
        ctx.stroke();
      });
      ripples = ripples.filter((r) => r.alpha > 0);

      // 1. Move and draw particles
      particles.forEach((p) => {
        // Apply cursor gravity/attraction force
        if (mouse.active) {
          const dx = mouse.x - p.x;
          const dy = mouse.y - p.y;
          const dist = Math.hypot(dx, dy);
          if (dist < mouseConnectionRadius) {
            // Soft pull towards cursor
            const force = (mouseConnectionRadius - dist) / mouseConnectionRadius;
            p.vx += (dx / dist) * force * 0.018;
            p.vy += (dy / dist) * force * 0.018;

            // Speed limit so they don't launch off
            const speed = Math.hypot(p.vx, p.vy);
            if (speed > 1.2) {
              p.vx = (p.vx / speed) * 1.2;
              p.vy = (p.vy / speed) * 1.2;
            }
          }
        }

        // Float movement
        p.x += p.vx;
        p.y += p.vy;

        // Bounce on boundaries
        if (p.x < 0 || p.x > w) p.vx *= -1;
        if (p.y < 0 || p.y > h) p.vy *= -1;

        // Keep inside screen
        if (p.x < 0) p.x = 0;
        if (p.x > w) p.x = w;
        if (p.y < 0) p.y = 0;
        if (p.y > h) p.y = h;

        // Draw particle
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(99, 102, 241, 0.18)";
        ctx.fill();
      });

      // 2. Draw connections (lines) between close particles
      for (let i = 0; i < particles.length; i++) {
        const p1 = particles[i];
        
        // Connect to mouse cursor
        if (mouse.active) {
          const dx = mouse.x - p1.x;
          const dy = mouse.y - p1.y;
          const dist = Math.hypot(dx, dy);
          if (dist < mouseConnectionRadius) {
            const factor = (mouseConnectionRadius - dist) / mouseConnectionRadius;
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(mouse.x, mouse.y);
            ctx.strokeStyle = `rgba(99, 102, 241, ${factor * 0.15})`;
            ctx.lineWidth = factor * 0.9;
            ctx.stroke();
          }
        }

        // Connect to other particles
        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const dist = Math.hypot(dx, dy);

          if (dist < maxDistance) {
            const factor = (maxDistance - dist) / maxDistance;
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            // Draw line
            ctx.strokeStyle = `rgba(99, 102, 241, ${factor * 0.1})`;
            ctx.lineWidth = factor * 0.6;
            ctx.stroke();
          }
        }
      }
    };

    const animate = () => {
      drawSimulation();
      animationFrameId = requestAnimationFrame(animate);
    };

    const handleMouseMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      mouse.active = true;

      // Spawn stardust sparks on mouse move
      if (Math.random() > 0.4) {
        sparks.push({
          x: e.clientX,
          y: e.clientY,
          vx: (Math.random() - 0.5) * 0.7,
          vy: (Math.random() - 0.5) * 0.7,
          radius: Math.random() * 2 + 0.8,
          alpha: 1.0
        });
      }
    };

    const handleMouseLeave = () => {
      mouse.active = false;
      mouse.x = -1000;
      mouse.y = -1000;
    };

    const handleClick = (e) => {
      const clickX = e.clientX;
      const clickY = e.clientY;

      // Spawn click ripple
      ripples.push({
        x: clickX,
        y: clickY,
        radius: 0,
        alpha: 1.0
      });

      // Spawn 18 explosion sparks
      for (let i = 0; i < 18; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 2.8 + 1.2;
        sparks.push({
          x: clickX,
          y: clickY,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          radius: Math.random() * 2.5 + 1.2,
          alpha: 1.0
        });
      }
    };

    window.addEventListener("resize", resizeCanvas);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("click", handleClick);
    document.addEventListener("mouseleave", handleMouseLeave);

    resizeCanvas();
    animate();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("resize", resizeCanvas);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("click", handleClick);
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  return (
    <>
      {/* Floating Gradient Orbs in Background */}
      <div className="blob-container">
        <div className="glowing-orb orb-1"></div>
        <div className="glowing-orb orb-2"></div>
        <div className="glowing-orb orb-3"></div>
      </div>
      {/* High-tech Constellation Canvas */}
      <canvas
        ref={canvasRef}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: -1,
          pointerEvents: "none",
          display: "block",
        }}
      />
    </>
  );
};

export default AmbientBackground;
